import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import "./Header.css";
import ReactGA from "react-ga4";
import axios from "axios";
import axiosInstance from "../axios";
import { API_BASE_URL } from "../config";

/**
 * Determines if a URL is an external link (starts with http:// or https://).
 */
const isExternalLink = url => {
  return url && (url.startsWith("http://") || url.startsWith("https://"));
};

/**
 * Renders a single menu link item using NavLink (for internal) or <a> (for external).
 */
const MenuLink = ({ item }) => {
  if (!item.url) return <span>{item.label}</span>;

  if (isExternalLink(item.url)) {
    return (
      <a
        href={item.url}
        target={item.open_in_new_tab ? "_blank" : "_self"}
        rel={item.open_in_new_tab ? "noopener noreferrer" : undefined}>
        {item.label}
      </a>
    );
  }

  return (
    <NavLink
      to={item.url}
      target={item.open_in_new_tab ? "_blank" : undefined}
      rel={item.open_in_new_tab ? "noopener noreferrer" : undefined}>
      {item.label}
    </NavLink>
  );
};

/**
 * Recursively renders a menu item and its children.
 */
const MenuItem = ({ item }) => {
  // Sub-dropdown (nested inside a dropdown)
  if (item.type === "sub-dropdown") {
    return (
      <li className="sub-dropdown">
        <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {item.label}
          {item.icon && (
            <img
              src={`/${item.icon}`}
              alt="icon"
              style={{ height: "15px", width: "15px", transform: "rotate(-90deg)" }}
            />
          )}
        </span>
        {item.children && item.children.length > 0 && (
          <ul className="sub-dropdown-content">
            {item.children.map(child => (
              <MenuItem key={child.id} item={child} />
            ))}
          </ul>
        )}
      </li>
    );
  }

  // Dropdown (top-level dropdown with children)
  if (item.type === "dropdown") {
    let extraClass = "";
    if (item.group_name === "signin") extraClass = " signin-btn";
    if (item.group_name === "register") extraClass = " register-btn";

    return (
      <li className={`dropdown${extraClass}`}>
        <span>
          {item.label} {item.icon && <img src={`/${item.icon}`} alt="icon" style={{ height: "15px", width: "15px" }} />}
        </span>
        {item.children && item.children.length > 0 && (
          <ul className="dropdown-content">
            {item.children.map(child => (
              <MenuItem key={child.id} item={child} />
            ))}
          </ul>
        )}
      </li>
    );
  }

  // Regular nav item (leaf node)
  return (
    <li className={item.is_highlighted ? "highlighted" : ""}>
      <MenuLink item={item} />
    </li>
  );
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const location = useLocation();

  ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_ID);

  useEffect(() => {
    if (JSON.parse(localStorage.getItem("homeshiksha-jwt"))) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
    setCurrentPath(() => window.location.pathname);
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname,
      title: "Home Page",
    });

    // ✅ Fetch dynamic menu items from API
    const fetchMenuItems = async () => {
      try {
        const response = await axiosInstance.get("/v1/header-menu?platform=student");
        if (response.data && response.data.is_success) {
          setMenuItems(response.data.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch header menu items:", error);
        setMenuItems([]);
      } finally {
        setMenuLoading(false);
      }
    };

    fetchMenuItems();
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const sendNotification = () => {
    axiosInstance.post("/v1/send-notification", {
      token:
        "d83nD3BgwDjkPgyWxPrVy-:APA91bHwl4MHcQLkAwu2Wz8t_3Is3lSkvD-1bX83Me0FnOemdE-gRiX8fNdpDTIapUdkXwWtmdr3DsGgvyoRIF5AJnO7Dmz_LxAzaFkjq37zSSINBb2h-yA",
      title: "Test Notification",
      message: "This is a test notification",
    });
  };

  // Separate menu items into center nav and right-side action buttons
  const centerMenuItems = menuItems.filter(item => item.group_name !== "signin" && item.group_name !== "register");
  const actionButtons = menuItems.filter(item => item.group_name === "signin" || item.group_name === "register");

  // For mobile: find the signin and register groups
  const signinGroup = menuItems.find(item => item.group_name === "signin");
  const registerGroup = menuItems.find(item => item.group_name === "register");

  return (
    <header className="main-header">
      {/* ✅ Left: Logo */}
      <div className="logo">
        <NavLink
          to={process.env.REACT_APP_OLD_HOMESHIKSHA}
          onClick={() => {
            setCurrentPath(() => "/dashboard");
          }}>
          <img src="/homeshiksha-logo.jpg" alt="logo" />
        </NavLink>
      </div>

      {/* ✅ Logged In → Desktop Nav */}
      {isLoggedIn ? (
        <>
          <nav className="main-nav">
            <ul className="nav-list">
              <li>
                <NavLink
                  to="/dashboard"
                  className={
                    location.pathname === "/dashboard" || location.pathname.startsWith("/deal-details") ? "active" : ""
                  }>
                  Dashboard
                </NavLink>
              </li>
              <li>
                <NavLink to="/demo" activeClassName="active">
                  Demo
                </NavLink>
              </li>
              <li>
                <NavLink to="/tutors" activeClassName="active">
                  Tutors
                </NavLink>
              </li>
            </ul>
          </nav>

          <div className="header-actions">
            <ul className="action-list">
              <li>
                <button
                  onClick={sendNotification}
                  style={{
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}>
                  <svg width="27" height="28" viewBox="0 0 27 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M11.5762 26.4277H15.4223"
                      stroke="#828282"
                      strokeWidth="1.71429"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M21.1931 11.1723C21.1931 9.01419 20.3827 6.94448 18.94 5.41846C17.4974 3.89247 15.5409 3.03516 13.5008 3.03516C11.4606 3.03516 9.50406 3.89247 8.06146 5.41846C6.61889 6.94448 5.80844 9.01419 5.80844 11.1723V18.2923C5.80844 19.1016 5.50452 19.8778 4.96356 20.4501C4.4226 21.0223 3.68887 21.3437 2.92383 21.3437H24.0777C23.3127 21.3437 22.5788 21.0223 22.0379 20.4501C21.4969 19.8778 21.1931 19.1016 21.1931 18.2923V11.1723Z"
                      stroke="#828282"
                      strokeWidth="1.71429"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 10.907C1.00104 8.97062 1.43757 7.06231 2.27354 5.33968C3.1095 3.61705 4.3209 2.12954 5.80769 1"
                      stroke="#828282"
                      strokeWidth="1.71429"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M26.0001 10.907C25.9991 8.97062 25.5626 7.06231 24.7266 5.33968C23.8907 3.61705 22.6791 2.12954 21.1924 1"
                      stroke="#828282"
                      strokeWidth="1.71429"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </li>
              <li className="dropdown profile-avatar">
                <img src="profile.jpg" alt="avatar" />
                <ul className="dropdown-content dropdown-content-profile">
                  <li>
                    <NavLink to={"/my-profile"} activeClassName="active">
                      My Profile
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to={"/logout"} activeClassName="active">
                      Logout
                    </NavLink>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </>
      ) : (
        <>
          {/* ✅ Center: Dynamic Menu Nav */}
          {!menuLoading && (
            <nav className="main-nav">
              <ul className="nav-list">
                {centerMenuItems.map(item => (
                  <MenuItem key={item.id} item={item} />
                ))}
              </ul>
            </nav>
          )}

          {/* ✅ Right: Action Buttons (SignIn / Register) */}
          {!menuLoading && actionButtons.length > 0 && (
            <div className="header-actions">
              <ul className="nav-list action-list">
                {actionButtons.map(item => (
                  <MenuItem key={item.id} item={item} />
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* ✅ Mobile Menu Icon */}
      <div className="mobile-menu-icon" onClick={toggleMobileMenu}>
        <img src="/menu.svg" alt="hamberger_menu" />
      </div>

      {isMobileMenuOpen && (
        <nav className="mobile-nav">
          {isLoggedIn ? (
            <ul className="nav-list" onClick={toggleMobileMenu}>
              <li>
                <NavLink to="/dashboard" activeClassName="active">
                  Dashboard
                </NavLink>
              </li>
              <li>
                <NavLink to="/tutors" activeClassName="active">
                  Confirmed Tutors
                </NavLink>
              </li>
              <li>
                <NavLink to="/demo" activeClassName="active">
                  Demo classes
                </NavLink>
              </li>
              <li>
                <NavLink to="/counselling" activeClassName="active">
                  Counselling
                </NavLink>
              </li>
              <li>
                <NavLink to="/my-requests" activeClassName="active">
                  My Requests
                </NavLink>
              </li>
              <li>
                <NavLink to="/my-profile" activeClassName="active">
                  My Profile
                </NavLink>
              </li>
              <li>
                <NavLink to="/address" activeClassName="active">
                  My Address
                </NavLink>
              </li>
              <li>
                <NavLink to={"/subscription"} activeClassName="active">
                  Subscription
                </NavLink>
              </li>
              <li>
                <NavLink to={"/faq"} activeClassName="active">
                  FAQ
                </NavLink>
              </li>
              <li>
                <NavLink to={"/policy"} activeClassName="active">
                  Policy
                </NavLink>
              </li>
              <li>
                <NavLink to={"/need-help"} activeClassName="active">
                  Need Help?
                </NavLink>
              </li>
              <li>
                <NavLink to={"/give-feedback"} activeClassName="active">
                  Feedback
                </NavLink>
              </li>
              <li>
                <NavLink to={"/logout"} activeClassName="active">
                  Logout
                </NavLink>
              </li>
            </ul>
          ) : (
            !menuLoading && (
              <ul>
                {/* Mobile Sign In / Register row */}
                <li className="mobile-li">
                  {signinGroup && signinGroup.children && signinGroup.children.length > 0 && (
                    <>
                      <MenuLink item={{ ...signinGroup.children[0], label: "Sign In" }} />/
                    </>
                  )}
                  {registerGroup && registerGroup.children && registerGroup.children.length > 0 ? (
                    <MenuLink
                      item={{ ...registerGroup.children[registerGroup.children.length - 1], label: "Register" }}
                    />
                  ) : (
                    <Link to="/need-a-tutor">Register</Link>
                  )}
                </li>

                {/* Render all menu items except signin and register groups */}
                {centerMenuItems.map(item => (
                  <MenuItem key={`mobile-${item.id}`} item={item} />
                ))}
              </ul>
            )
          )}
        </nav>
      )}
    </header>
  );
};

export default Header;
